import React from "react";
import config from "@config/config";
import {
  useCategoryProductsQuery,
  useAttachCategoryProductMutation,
  useDetachCategoryProductMutation,
  useProductsQuery,
} from "@queries/catalog";

const imgUrl = (p) =>
  !p ? "" : /^https?:\/\//.test(p) ? p : `${config.imageBaseUrl}${p}`;

// Manage which products belong to a category (e.g. the Shop): list current
// members with a Remove button, and search the catalog to add more. Changes
// write straight to oc_product_to_category, so the website shop updates.
export default function CategoryProductsManager({ categoryId }) {
  const { data: products = [], isLoading } = useCategoryProductsQuery(categoryId);
  const attach = useAttachCategoryProductMutation();
  const detach = useDetachCategoryProductMutation();

  const [search, setSearch] = React.useState("");
  const searchEnabled = search.trim().length >= 2;
  const { data: searchData } = useProductsQuery(
    searchEnabled ? { search: search.trim() } : {},
    1,
    8
  );
  const results = searchEnabled ? searchData?.data || [] : [];
  const inCat = new Set(products.map((p) => Number(p.product_id)));

  const thumb = (src) => (
    <div style={{ width: 44, height: 44, borderRadius: 6, background: "#f3f4f6", border: "1px solid #e5e7eb", overflow: "hidden", flexShrink: 0 }}>
      {src ? <img src={imgUrl(src)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
    </div>
  );

  return (
    <div className="oc-card" style={{ marginTop: 20 }}>
      <div className="oc-header">
        <h2>Products in this category ({products.length})</h2>
      </div>

      {/* Add a product */}
      <div style={{ position: "relative", maxWidth: 480, marginBottom: 16 }}>
        <label style={{ fontSize: 12, color: "#6b7280", display: "block", marginBottom: 4 }}>
          Add a product (search by name or model)
        </label>
        <input
          className="oc-input"
          placeholder="Type at least 2 characters…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {searchEnabled && results.length > 0 && (
          <div style={{ position: "absolute", zIndex: 20, left: 0, right: 0, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, marginTop: 4, boxShadow: "0 10px 30px rgba(0,0,0,.12)", maxHeight: 320, overflowY: "auto" }}>
            {results.map((p) => {
              const already = inCat.has(Number(p.id));
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={already || attach.isPending}
                  onClick={() => attach.mutate({ id: categoryId, productId: p.id }, { onSuccess: () => setSearch("") })}
                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", padding: "8px 10px", border: "none", borderBottom: "1px solid #f1f5f9", background: already ? "#f8fafc" : "#fff", cursor: already ? "default" : "pointer" }}
                >
                  {thumb(p.image)}
                  <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{p.name || `#${p.id}`}</span>
                    <span style={{ display: "block", fontSize: 11, color: "#94a3b8" }}>#{p.id} · {p.model}</span>
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: already ? "#94a3b8" : "#16a34a" }}>
                    {already ? "Added" : "+ Add"}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Current members */}
      {isLoading ? (
        <div className="oc-empty">Loading…</div>
      ) : products.length === 0 ? (
        <div className="oc-empty">No products in this category yet. Use the search above to add some.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {products.map((p) => (
            <div key={p.product_id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 10px", border: "1px solid #eef2f7", borderRadius: 8 }}>
              {thumb(p.image)}
              <span style={{ flex: 1 }}>
                <a href={`/catalog/products/${p.product_id}`} target="_blank" rel="noreferrer" style={{ fontWeight: 600, fontSize: 14 }}>
                  {p.name || `#${p.product_id}`}
                </a>
                <span style={{ display: "block", fontSize: 11, color: "#94a3b8" }}>
                  #{p.product_id} · {p.model} · {Number(p.price).toFixed(2)} JOD
                  {Number(p.status) !== 1 ? " · (disabled)" : ""}
                </span>
              </span>
              <a href={`/catalog/products/${p.product_id}?tab=data`} target="_blank" rel="noreferrer" className="oc-btn" style={{ fontSize: 12 }}>
                Edit images
              </a>
              <button
                type="button"
                disabled={detach.isPending}
                onClick={() => {
                  if (window.confirm(`Remove "${p.name || p.product_id}" from this category?`)) {
                    detach.mutate({ id: categoryId, productId: p.product_id });
                  }
                }}
                style={{ border: "1px solid #f0caca", background: "#fff", color: "#dc2626", borderRadius: 6, cursor: "pointer", padding: "4px 10px", fontSize: 12, fontWeight: 700 }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
